import { useState } from 'react';
import type { ReactNode } from 'react';
import { Modal } from '@/components/dialogs/Modal';

interface CommentConfirmDialogProps {
  show: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmVariant?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: (comment: string) => void;
  onCancel: () => void;
}

// Replacement for the legacy SweetAlert comment prompt (input:'textarea') used by
// Lock/Unlock: a Modal with a required textarea; confirm is disabled until a
// non-empty comment is entered (validator "Please add a comment.").
export function CommentConfirmDialog({
  show,
  title,
  body,
  confirmLabel,
  confirmVariant = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: CommentConfirmDialogProps) {
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = comment.trim();
  const invalid = trimmed.length === 0;

  function reset() {
    setComment('');
    setTouched(false);
  }

  function handleConfirm() {
    if (invalid) {
      setTouched(true);
      return;
    }
    onConfirm(trimmed);
    reset();
  }

  function handleCancel() {
    reset();
    onCancel();
  }

  return (
    <Modal
      show={show}
      title={title}
      onClose={handleCancel}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={handleCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn btn-${confirmVariant}`}
            onClick={handleConfirm}
            disabled={busy || invalid}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="mb-3">{body}</div>
      <label className="form-label">Comment</label>
      <textarea
        className={`form-control${touched && invalid ? ' is-invalid' : ''}`}
        rows={3}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      {touched && invalid && <div className="invalid-feedback d-block">Please add a comment.</div>}
    </Modal>
  );
}
